import {useEffect, useRef, useState} from 'react';
import {Button, Divider, Menu, MenuItem, Tooltip} from '@mui/material';
import useParseURL from 'hooks/useParseURL';
import {EMPTY_ID, useGetPlans} from 'hooks/swr';
import {useAuth0} from '@auth0/auth0-react';
import * as I from 'interfaces';
import type {Plan} from 'client';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ListSubheader from '@mui/material/ListSubheader';
import {useTranslation} from 'react-i18next';
import {useSWRConfig, unstable_serialize} from 'swr';
import backendCommunicationInstance from 'services/backendCommunicator';
import {useOnboardingContext} from 'context/OnboaringContext';
import {filterRecentPlans} from './AppTopBarPlansMenuHelpers';
import {usePlansFilterState} from 'components/Shared/UserPlans';

export function AppTopBarPlansMenu({title}: {title: string}) {
  const myPlans = usePlansFilterState((state) => state.filter.myPlans);
  const {cache} = useSWRConfig();
  const {t} = useTranslation('common');
  const onboardingContext = useOnboardingContext();
  const anchorRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (onboardingContext.enabled) {
      setOpen(onboardingContext.isPlanMenuOpen ?? false);
    }
  }, [onboardingContext]);

  const handleToggle = () => {
    setOpen(!open);
    onboardingContext.enabled = false;
  };

  const handleClose = () => {
    setOpen(false);
    onboardingContext.enabled = false;
  };

  const {navigate, urlSearchParams, planId, language, pathname} = useParseURL();

  const {data} = useGetPlans({resourceId: EMPTY_ID});
  const {user} = useAuth0();
  const [loadingPlan, setLoadingPlan] = useState<boolean>(false);
  const plans: Plan[] = filterRecentPlans(data, user, myPlans, planId);
  const _urlSearchParams = new URLSearchParams();
  if (language) {
    _urlSearchParams.set(I.URLParams.language, language);
  }

  const navigateToList = () => {
    handleClose();
    navigate({
      pathname: I.URLPathname.root,
      search: _urlSearchParams.toString(),
    });
  };

  return (
    <div>
      <Tooltip title={t('selectPlan') ?? ''}>
        <Button
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
          onClick={handleToggle}
          endIcon={<ArrowDropDownIcon />}
          data-testid="plans-menu"
          size="small"
          color="inherit"
          ref={anchorRef}
          loading={loadingPlan}
          loadingPosition="end"
        >
          {title}
        </Button>
      </Tooltip>
      <Menu
        anchorEl={anchorRef.current}
        open={open}
        onClose={handleClose}
        style={{zIndex: 3000}}
        slotProps={{
          list: {
            id: 'onboarding-main-plans-menu',
          },
        }}
      >
        <ListSubheader>{t('recentPlans')}</ListSubheader>
        <Divider variant="middle" />
        {plans &&
          plans.map((plan) => (
            <MenuItem
              key={plan.id}
              onClick={() => {
                handleClose();
                setLoadingPlan(true);
                void backendCommunicationInstance
                  .getPlanSimple(plan.id)
                  .then(({data}) => {
                    cache.set(unstable_serialize([plan.id, I.SWRKeyEnum.GET_PLAN]), {data});
                    urlSearchParams.set(I.URLParams.plan, plan.id);
                    urlSearchParams.delete(I.URLParams.site);
                    urlSearchParams.delete(I.URLParams.cage);
                    navigate({
                      pathname: !pathname.startsWith(I.URLPathname.plan) ? I.URLPathname.planOverview : pathname,
                      search: urlSearchParams.toString(),
                    });
                  })
                  .finally(() => {
                    setLoadingPlan(false);
                  });
              }}
              data-testid={`plan-menu-item-${plan.id}`}
            >
              {plan.name}
            </MenuItem>
          ))}
        <Divider variant="middle" />
        <Button sx={{ml: 1}} onClick={navigateToList}>
          {t('allPlans')}
        </Button>
      </Menu>
    </div>
  );
}
